import _ from 'lodash';
import Renderer from './render';
import DataProcessor from './data';
import Filter from './filter';
import constants from '$$helpers/constants';
import ganttPreloader from '../../helpers/preloader';

class Core {
  constructor() {
    this.ganttInstance = null;
    this.workloadStore = null;
    this.workloadWorker = null;
    this.cachedWorkerData = {};
    this.cachedParseData = null;
    this.cacheMap = {};
  }

  init(instance, config) {
    this.config = {
      RESOURCE_TASK_ID_POSTFIX: '-tasks',
      ROW_HEIGHT: 30,
      RESOURCE_TASK_HEIGHT: 22,
      RESOURCE_TASK_SMALL_HEIGHT: 4,
      TASK_BAR_TOP_POSITION: 4,
      PARTIAL_ROWS_COUNT: 20,
      resourcesLayout: {},
      openedResources: {},
      selectedTask: {},
      countOfUnassignedTaskRows: 20,
      taskCellsEditMode: {},
      showPreloader: true,
      ...config,
    };

    this.workloadStore = instance.createDatastore({
      name: 'workloadStore',
    });

    DataProcessor.init(instance, this.config);
    Renderer.init(instance, this.config);
    Filter.init(instance, this.config);

    this.ganttInstance = instance;

    this.initWorkers();

    this.clearCache();

    this.calculateAndRender();
  }

  async calculateAndRender(needResize = true) {
    if (!this._validateCalculateAndRender()) {
      return;
    }

    if (this.config.showPreloader) {
      ganttPreloader.show();
      this.config.showPreloader = false;
    }

    let dataForParse = [];
    const preparedData = DataProcessor.prepareData();

    // if (!preparedData.length) {
    //   return;
    // }

    const cached = DataProcessor.applyCacheToData(preparedData, this.cachedWorkerData);

    if (cached.needReCalculate) {
      const workerConfig = DataProcessor.prepareWorkerConfig();

      const workerData = {
        innerData: cached.data,
        ...workerConfig,
      };

      const calculatedData = await this.workloadWorker(_.cloneDeep(workerData));

      this._updateCacheData(calculatedData);

      this.config.resourcesLayout = calculatedData.resourcesLayout;
      dataForParse = calculatedData.resourcesData;
    } else {
      dataForParse = cached.data;
    }

    dataForParse = DataProcessor.prepareDataForSmartScroll(dataForParse);

    Renderer.beforeRender();

    this.cachedParseData = dataForParse;

    this.workloadStore.clearAll();
    this.workloadStore.parse(dataForParse);

    Renderer.afterRender(!!dataForParse.length, needResize);

    this.lastTimelineStart = this.ganttInstance.config.start_date;
    this.lastTimelineEnd = this.ganttInstance.config.end_date;

    ganttPreloader.hide();
  }

  _validateCalculateAndRender() {
    if (this.config.type === constants.WORKLOAD_TYPES.panel) {
      this.config.showPreloader = false;

      if (this.ganttInstance.config.hide_workload) {
        return;
      }

      if (this.ganttInstance.config.durationData.mode === 'hour') {
        return;
      }
    }

    if (this.ganttInstance.$destroyed) {
      return;
    }

    return true;
  }

  refresh() {
    if (this.cachedParseData) {
      const prepareData = DataProcessor.prepareDataForSmartScroll(this.cachedParseData);

      Renderer.beforeRender();

      this.workloadStore.clearAll();
      this.workloadStore.parse(prepareData);

      Renderer.afterRender(!!prepareData.length);
    }
  }

  clearCache(updatedData) {
    if (updatedData) {
      if (updatedData.taskIds) {
        updatedData.taskIds.forEach(taskId => {
          if (this.cacheMap[taskId]) {
            Object.keys(this.cacheMap[taskId]).forEach(resourceDataId => {
              this.clearCachedWorkerDataById(resourceDataId);
            });
          }
        });
        // TODO think about optimization unassigned section
        this.clearCachedWorkerDataById('unassigned');
        this.clearCachedWorkerDataById('unassigned-tasks');
      }
      if (updatedData.resourceIds) {
        updatedData.resourceIds.forEach(resourceId => {
          this.clearCachedWorkerDataById(resourceId);
        });
      }

      if (!updatedData.taskIds && !updatedData.resourceIds) {
        this.cachedWorkerData = {};
      }
    } else {
      this.cachedWorkerData = {};
    }

    this.cachedParseData = null;
  }

  clearCachedWorkerDataById(id) {
    if (this.cachedWorkerData[id]) {
      this.cachedWorkerData[id] = null;
    }
  }

  checkIsUpdatedTimeline() {
    if (!Object.keys(this.ganttInstance.$data.tasksStore.pull).length) {
      return;
    }
    if (!this.lastTimelineStart && !this.lastTimelineEnd) {
      return;
    }
    if (this.ganttInstance.config.start_date?.valueOf() !== this.lastTimelineStart.valueOf()
      || this.ganttInstance.config.end_date?.valueOf() !== this.lastTimelineEnd.valueOf()) {
      this.clearCache();

      return true;
    }
  }

  render() {
    Renderer.refresh();
  }

  changeCalculationMode(value) {
    this.config.calculationMode = value;
    this.clearCache();
    this.calculateAndRender(false);
  }

  changeZoom(zoom) {
    if (!this.config) {
      return;
    }

    this.config.zoom = zoom;
    this.config.openedResources = {};
    this.config.selectedTask = {};
    this.clearCache();
    this.calculateAndRender();
  }

  changeViewMode(value) {
    this.config.viewMode = value;
    this.clearCache();
    this.calculateAndRender();
  }

  initWorkers() {
    let version = Date.now();

    if (GT.productVersion && GT.productVersion.buildVersion) {
      version = GT.productVersion.buildVersion;
    }

    if (!this.workloadWorker) {
      this.workloadWorker = this._createWorker(`/workers/workload_worker.js?${version}`);
    }
  }

  _createWorker(workerPath) {
    const worker = new Worker(workerPath);

    return function exec(workerData) {
      return new Promise((resolve, reject) => {
        worker.onmessage = e => {
          worker.onmessage = null;
          worker.onerror = null;

          return resolve(e.data);
        };

        worker.onerror = e => {
          worker.onmessage = null;
          worker.onerror = null;

          return reject(e);
        };

        worker.postMessage(workerData);
      });
    };
  }

  _updateCacheData(calculatedData) {
    this.cacheMap = {};

    calculatedData.resourcesData.forEach(resourceData => {
      this.cachedWorkerData[resourceData.id] = resourceData;

      resourceData.tasksByGanttId.forEach(data => {
        if (!this.cacheMap[data.task_id]) {
          this.cacheMap[data.task_id] = { [resourceData.id]: true };
        } else {
          this.cacheMap[data.task_id][resourceData.id] = true;
        }
      });
    });
  }

  isWorkloadPanelType() {
    return this.config.type === constants.WORKLOAD_TYPES.panel;
  }
}

export default new Core();
